import { Warning, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { OnboardingApiError, OnboardingLabels } from "./types";

export interface OnboardingErrorBannerProps {
  error: OnboardingApiError;
  labels: OnboardingLabels;
  onDismiss?: () => void;
  onLogin?: () => void;
  onContactSupport?: () => void;
  className?: string;
}

export function OnboardingErrorBanner({
  error,
  labels,
  onDismiss,
  onLogin,
  onContactSupport,
  className,
}: OnboardingErrorBannerProps) {
  const message = error.message || labels.errors[error.code as keyof typeof labels.errors] || labels.errors.provisioning_failed;

  let actionLabel: string | null = null;
  let actionHandler: (() => void) | null = null;

  if (error.code === "email_already_exists" && onLogin) {
    actionLabel = labels.loginLink;
    actionHandler = onLogin;
  } else if (error.code === "tax_id_already_exists" && onContactSupport) {
    actionLabel = "Support";
    actionHandler = onContactSupport;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "flex items-start gap-3 rounded-biz-card border border-biz-danger/30 bg-biz-danger/5 p-4",
        "animate-in fade-in-0 duration-150",
        "motion-reduce:animate-none",
        className,
      )}
    >
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-biz-danger/10 text-biz-danger">
        <Warning size={18} aria-hidden />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-biz-danger">{message}</p>
        {error.fieldErrors && error.fieldErrors.length > 0 && (
          <ul className="mt-1.5 list-inside list-disc text-xs text-biz-danger/80">
            {error.fieldErrors.map((fe, i) => (
              <li key={i}>
                <span className="font-mono">{fe.path}</span>: {fe.message}
              </li>
            ))}
          </ul>
        )}
        {actionLabel && actionHandler && (
          <button
            type="button"
            onClick={actionHandler}
            className="mt-2 inline-flex h-9 items-center rounded-md border border-biz-danger/40 bg-biz-background px-3 text-xs font-semibold text-biz-danger transition-colors hover:bg-biz-danger hover:text-biz-danger-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-danger focus-visible:ring-offset-2"
          >
            {actionLabel}
          </button>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-biz-danger/70 hover:bg-biz-danger/10 hover:text-biz-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-danger"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
